// Minimal schema-effect extractor (SqlGrammar2 PRD:320, deep review §4.6
// prerequisite). A targeted token scan — NOT a tree walk. It reacts only to the
// five schema-mutating verbs (CREATE / ALTER / DROP / TRUNCATE / RENAME): verb →
// effect, skip modifier noise, map the object keyword onto SchemaObjectKind,
// then read the qualified name via the shared per-engine quote-aware
// `readQualifiedName`. Everything else returns `[]`.
//
// OUT of scope (needs identifier binding — #13 territory): clause-level RENAME
// detail on ALTER (`ALTER … RENAME TO x` → effect `alter`), column/constraint
// targets, multi-object TRUNCATE beyond the first table. NEVER throws: an
// unrecognized object tail → `objectKind:'unknown'`, a bare verb → `[]`.

import type { ParseOptions, QualifiedName, SchemaEffect, SchemaObjectKind, SqlEngine } from '../model';
import { readQualifiedName } from '../../sql-resolver/scope/identifiers';
import { stripLeadingCommentsAndWs } from './coarseKind';

type Verb = 'create' | 'alter' | 'drop' | 'truncate' | 'rename';

const VERB_MAP: Record<string, Verb> = {
  CREATE: 'create',
  ALTER: 'alter',
  DROP: 'drop',
  TRUNCATE: 'truncate',
  RENAME: 'rename',
};

// Object keyword → SchemaObjectKind. PROCEDURE/PROC/FUNCTION/PACKAGE/EVENT all
// collapse to `routine`; anything absent here degrades to `unknown`.
const OBJECT_KIND: Record<string, SchemaObjectKind> = {
  TABLE: 'table', VIEW: 'view', INDEX: 'index',
  PROCEDURE: 'routine', PROC: 'routine', FUNCTION: 'routine', PACKAGE: 'routine', EVENT: 'routine',
  TRIGGER: 'trigger', SEQUENCE: 'sequence', SCHEMA: 'schema', DATABASE: 'database',
  SYNONYM: 'synonym', EXTENSION: 'extension', TYPE: 'type', POLICY: 'policy', ROLE: 'role',
};

// Modifier words between the verb and the object keyword (the brief's verbatim
// list). `DEFINER=…` is handled separately (its principal is not a bare word).
const PRE_KEYWORD_NOISE = new Set([
  'OR', 'REPLACE', 'ALTER', 'TEMP', 'TEMPORARY',
  'UNIQUE', 'CLUSTERED', 'NONCLUSTERED', 'MATERIALIZED', 'GLOBAL', 'LOCAL',
]);

// Words between the object keyword and the name (`IF [NOT] EXISTS`, pg `CONCURRENTLY`).
const PRE_NAME_NOISE = new Set(['IF', 'NOT', 'EXISTS', 'CONCURRENTLY']);

function skipTrivia(text: string, from: number): number {
  const n = text.length;
  let i = from;
  while (i < n) {
    const c = text[i];
    if (/\s/.test(c)) { i++; continue; }
    if (c === '-' && text[i + 1] === '-') {
      const nl = text.indexOf('\n', i);
      i = nl < 0 ? n : nl + 1;
      continue;
    }
    if (c === '/' && text[i + 1] === '*') {
      const end = text.indexOf('*/', i);
      i = end < 0 ? n : end + 2;
      continue;
    }
    break;
  }
  return i;
}

// Next bare word (uppercased), skipping leading trivia. Null when the next token
// is not a bare word (a quoted/bracketed identifier or EOF).
function readWord(text: string, from: number): { word: string; end: number } | null {
  const i = skipTrivia(text, from);
  if (i >= text.length || !/[A-Za-z_]/.test(text[i])) return null;
  let j = i + 1;
  while (j < text.length && /[A-Za-z0-9_]/.test(text[j])) j++;
  return { word: text.slice(i, j).toUpperCase(), end: j };
}

// Skip a `DEFINER = principal` clause. The principal is a quoted/bare user,
// optionally `@host`, or `CURRENT_USER[()]`.
function skipDefiner(text: string, from: number): number {
  let i = skipTrivia(text, from);
  if (text[i] === '=') i = i + 1;
  i = skipPrincipal(text, i);
  const at = skipTrivia(text, i);
  if (text[at] === '@') i = skipPrincipal(text, at + 1);
  return i;
}

function skipPrincipal(text: string, from: number): number {
  let i = skipTrivia(text, from);
  const c = text[i];
  if (c === "'" || c === '`' || c === '"') {
    i++;
    while (i < text.length) {
      if (text[i] === c) {
        if (c === "'" && text[i + 1] === c) { i += 2; continue; } // '' escape
        i++;
        break;
      }
      i++;
    }
    return i;
  }
  while (i < text.length && /[A-Za-z0-9_$]/.test(text[i])) i++;
  const paren = skipTrivia(text, i);
  if (text[paren] === '(') {
    let k = paren;
    while (k < text.length && text[k] !== ')') k++;
    return k < text.length ? k + 1 : k;
  }
  return i;
}

function readName(text: string, from: number, options: ParseOptions): { qn: QualifiedName; end: number } | null {
  const start = skipTrivia(text, from);
  const read = readQualifiedName(text, start, text.length, options);
  if (!read) return null;
  return { qn: read.qualified, end: read.range.endOffset };
}

function emptyName(): QualifiedName {
  return { name: '', foldedName: '', quoted: false, raw: '' };
}

function extractCreateDropAlter(
  text: string,
  effect: SchemaEffect['effect'],
  from: number,
  options: ParseOptions,
): SchemaEffect[] {
  let i = from;
  // Skip modifier noise (+ DEFINER) up to the object keyword.
  let w = readWord(text, i);
  while (w && (PRE_KEYWORD_NOISE.has(w.word) || w.word === 'DEFINER')) {
    i = w.word === 'DEFINER' ? skipDefiner(text, w.end) : w.end;
    w = readWord(text, i);
  }
  if (!w) return []; // bare verb, nothing to describe

  let objectKind = OBJECT_KIND[w.word] ?? 'unknown';
  i = w.end;
  // Two-word object kind: PROPERTY GRAPH → 'property-graph'. Without this,
  // PROPERTY degrades to 'unknown' and GRAPH is misread as the target name.
  if (w.word === 'PROPERTY') {
    const g = readWord(text, i);
    if (g && g.word === 'GRAPH') {
      objectKind = 'property-graph';
      i = g.end;
    }
  }
  if (w.word === 'PACKAGE' || w.word === 'TYPE') {
    const body = readWord(text, i);
    if (body && body.word === 'BODY') i = body.end;
  }

  let nm = readWord(text, i);
  while (nm && PRE_NAME_NOISE.has(nm.word)) { i = nm.end; nm = readWord(text, i); }

  const target = readName(text, i, options)?.qn ?? emptyName();
  return [{ effect, objectKind, target }];
}

function extractTruncate(text: string, from: number, options: ParseOptions): SchemaEffect[] {
  let i = from;
  let w = readWord(text, i);
  while (w && (w.word === 'TABLE' || w.word === 'ONLY')) { i = w.end; w = readWord(text, i); }
  const target = readName(text, i, options);
  if (!target) return [];
  return [{ effect: 'truncate', objectKind: 'table', target: target.qn }];
}

function extractRename(text: string, from: number, options: ParseOptions): SchemaEffect[] {
  let i = from;
  const head = readWord(text, i);
  if (head && head.word === 'TABLE') i = head.end; // mysql `RENAME TABLE …`
  // Oracle `RENAME old TO new` has no object keyword; objectKind defaults to table.

  const effects: SchemaEffect[] = [];
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const oldName = readName(text, i, options);
    if (!oldName) break;
    i = oldName.end;
    const to = readWord(text, i);
    if (!to || to.word !== 'TO') {
      effects.push({ effect: 'rename', objectKind: 'table', target: oldName.qn });
      break;
    }
    i = to.end;
    const newName = readName(text, i, options);
    if (!newName) {
      effects.push({ effect: 'rename', objectKind: 'table', target: oldName.qn });
      break;
    }
    i = newName.end;
    effects.push({ effect: 'rename', objectKind: 'table', target: newName.qn, previousName: oldName.qn });
    const comma = skipTrivia(text, i);
    if (text[comma] === ',') { i = comma + 1; continue; } // mysql multi-pair
    break;
  }
  return effects;
}

// Extract the schema effects a statement declares. `[]` for anything that is not
// a CREATE / ALTER / DROP / TRUNCATE / RENAME. Never throws.
export function extractSchemaEffects(text: string, engine: SqlEngine): SchemaEffect[] {
  const stripped = stripLeadingCommentsAndWs(text);
  const first = readWord(stripped, 0);
  if (!first) return [];
  const verb = VERB_MAP[first.word];
  if (!verb) return [];

  const options: ParseOptions = { engine };
  if (verb === 'rename') return extractRename(stripped, first.end, options);
  if (verb === 'truncate') return extractTruncate(stripped, first.end, options);
  return extractCreateDropAlter(stripped, verb, first.end, options);
}
